import type { UploadTelemetry } from "./ports";
import { isConfirmedMigrationIntegrityError } from "./driverUrlRefresh";
import { ChunkNetworkError, UploadCancelledError, UploadOwnershipLostError } from "./uploadChunk";
import { isAndroidBrowser } from "./pickedFileReadGate";

const MIN_CONCURRENCY = 1;

/**
 * LSP-18795: how long a batch whose first verdict is TRANSIENT waits for an in-flight sibling to
 * report a stronger, permanent one before giving up and throwing the transient verdict.
 *
 * Why a bound at all: uploadChunk's offline branch awaits `waitForOnline(signal)` and then
 * `continue`s WITHOUT incrementing `attempt`, so a parked chunk is not budget-bounded — it can sit
 * there for hours. Waiting on it unconditionally would mean a terminal failure is never reported
 * while the device is offline, which is a worse regression than the bug being fixed.
 *
 * WHAT TEN SECONDS DOES AND DOES NOT COVER (corrected after review — the original comment here
 * claimed it covered a sibling's remaining budget "with room to spare", which is wrong):
 *   - Covers: the retry BACKOFF between a sibling's remaining attempts. MAX_CHUNK_RETRIES = 3 over a
 *     computeRetryAt curve capped at 8s for `retry` totals a few seconds.
 *   - Does NOT cover: the in-flight PUT transfer itself, which is unbounded on a throttled link, nor
 *     a multipart URL-refresh backoff, whose cap is 30_000ms — three times this window.
 *
 * So the residual is real and is a deliberate trade-off, not an oversight: a sibling that fails
 * permanently LATER than this window still loses its verdict, the batch reports the transient, and
 * the drain re-picks the session. This narrows the defect rather than closing it completely —
 * siblings behind the same middlebox usually fail on similar timescales, which is the case that
 * matters, and the original bug lost the verdict whenever a transient merely landed first.
 *
 * The residual is observable rather than silent: `recording.fileUpload.chunkPermanentVerdict` still
 * fires when the late verdict arrives, carrying `supersededCode`, so a session that hit grace expiry
 * is greppable instead of looking like a plain transient failure. Pinned by the
 * "logs a late permanent verdict for diagnostics without changing the outcome the caller already
 * got" test.
 *
 * The principled fix is to scope the wait to the actual hazard instead of guessing a duration — wait
 * for in-flight siblings while ONLINE (where uploadChunk's budget genuinely bounds them) and cut the
 * wait only when offline. That needs an `isOnline` probe plumbed in from driverChunkLoop, so it is
 * raised for the author rather than taken unilaterally on an upload path.
 *
 * Note this window is only ever entered on the transient-first path. A permanent verdict cannot be
 * outranked, so a batch that fails permanently first still rejects with no added latency at all.
 */
const VERDICT_UPGRADE_GRACE_MS = 10_000;

/**
 * setTimeout stores its delay in a signed 32-bit int, and a larger value is silently coerced to 1ms
 * — the opposite of what the caller asked for (break-it round 6). Clamping keeps an over-large grace
 * behaving like a long wait instead of like no wait at all, which is what collapsed the upgrade
 * window and restored the first-wins bug. Guarding only the NaN/negative end of the range was a
 * half-guard; this is the other end of it.
 */
const MAX_TIMER_DELAY_MS = 2_147_483_647;

/**
 * LSP-18795: the permanence test, named so the rule is greppable rather than an inline instanceof.
 *
 * ChunkNetworkError is this module's only transient class — it is the one recordDrop and the requeue
 * path are built around, and every other error already lands in the immediately-terminal branch. So
 * "not a ChunkNetworkError" IS permanence here, and expressing it as the negative means a future
 * permanent class (uploadChunk.ts's MissingEtagError, LSP-18742) inherits the priority automatically
 * instead of silently re-acquiring the first-wins bug this ticket fixed. Mirrors uploadChunk.ts's own
 * isPermanentChunkError, which is written as a predicate for the same reason.
 */
/**
 * "No verdict yet", distinct from every value a processor can throw (LSP-18795 break-it round 1).
 *
 * `undefined` cannot serve as this sentinel and truthiness cannot serve as the test: a processor may
 * throw `undefined`, `null`, or `0`, and treating those as "no verdict" made a batch that FAILED
 * return cleanly — the driver would then mark the upload complete with chunks missing. That is the
 * same silent-success class this ticket exists to remove, so the emptiness test is identity against
 * a private symbol no caller can produce.
 */
// Typed `unknown` rather than `unique symbol` deliberately. With a unique-symbol type, TypeScript's
// control-flow analysis sees only the initializer inside processWorker — it cannot see the assignment
// that happens in setTerminalError's closure — so it concludes these variables can only BE the
// sentinel, and `!== NO_VERDICT` narrows them to `never`. That both breaks `instanceof`/`.name` on a
// verdict and, worse, reads to the next person as though the branch were unreachable.
const NO_VERDICT: unknown = Symbol("no verdict");

function isChunkNetworkError(error: unknown): boolean {
	try {
		return error instanceof ChunkNetworkError;
	} catch {
		// `instanceof` walks the value's prototype chain, so a Proxy with a throwing getPrototypeOf trap
		// makes even this classification throw (break-it round 3). Unclassifiable is treated as
		// NOT-transient, which matches what the pool already did with every non-ChunkNetworkError: route
		// it to the immediately-terminal branch. The alternative — guessing "transient" — would retry a
		// verdict we cannot read, which is the unbounded-retry defect this ticket removes.
		return false;
	}
}

/**
 * "Stop touching this session" signals, which are NOT chunk verdicts (AI review of 80d6b955b3).
 *
 * driverChunkLoop catches UploadOwnershipLostError out of this pool and returns the benign
 * `{ outcome: "yielded" }`; UploadCancelledError is the deliberate-abort path. Neither says anything
 * about whether a chunk can succeed. Ranking them as permanent — which "not a ChunkNetworkError" did
 * — let them OUTRANK a real transient verdict, so the pool threw UOLE and the driver reported a
 * polite yield for a batch that had actually failed on the network: no classifyFailure, no retry
 * classification. That is a silent failure introduced by the ranking, reachable on any mid-batch
 * ownership handoff, so they are excluded from permanence and instead DOMINATE as terminal verdicts:
 * abandon beats diagnose, and nothing should override an instruction to stop.
 */
function isAbandonSignal(error: unknown): boolean {
	try {
		return error instanceof UploadOwnershipLostError || error instanceof UploadCancelledError;
	} catch {
		return false;
	}
}

function isPermanentVerdict(error: unknown): boolean {
	return !isChunkNetworkError(error) && !isAbandonSignal(error);
}

/**
 * Total against hostile throwables, like isChunkNetworkError (AI review of 80d6b955b3, finding 4).
 *
 * These label expressions used to be bare `instanceof`/`.name`, so for the very Proxy the round-3
 * test throws — a getPrototypeOf trap that throws — recordPermanentVerdict threw, reportSafely
 * swallowed it, and the diagnostic was silently lost in exactly the hostile case the classifier was
 * made total to survive.
 */
function safeErrorLabel(value: unknown): string {
	try {
		return value instanceof Error ? value.name : typeof value;
	} catch {
		return "unreadable";
	}
}

/**
 * Telemetry must never change an upload's outcome (break-it round 3).
 *
 * recordDrop and recordPermanentVerdict run INSIDE the worker catch and call an app-supplied sink. A
 * throwing sink used to escape the catch before setTerminalError ran, so the real chunk verdict was
 * replaced by the logging error — and worse, a progress-backed network drop was never requeued, so a
 * failure a retry would have fixed became a hard one. Reporting a failure cannot be allowed to cause
 * one.
 */
function reportSafely(report: () => void): void {
	try {
		report();
	} catch {
		// Deliberately swallowed and deliberately not re-logged: the logger is what just failed.
	}
}

/** A one-shot resolve-only signal. Never rejects, so racing it cannot become an unhandled rejection. */
function createSignal(): { observed: Promise<void>; signal: () => void } {
	let signal = (): void => undefined;
	const observed = new Promise<void>((resolve) => {
		signal = resolve;
	});
	return { observed, signal };
}

/**
 * LSP-17008: how many FILES upload at once — used by both the fresh-pick path and the
 * offline-recovery drain so a recovered backlog never drains narrower than a fresh selection.
 * LSP-22818: 2 on Android, where 3+ concurrent reads of Photos-picker files break them.
 */
export const FILE_UPLOAD_CONCURRENCY = isAndroidBrowser() ? 2 : 3;
let availableFileUploadSlots = FILE_UPLOAD_CONCURRENCY;
const fileUploadSlotWaiters: Array<() => void> = [];
const RAMP_UP_AFTER = 5;

export async function withFileUploadConcurrencySlot(processor: () => Promise<void>): Promise<void> {
	if (availableFileUploadSlots > 0) {
		availableFileUploadSlots--;
	} else {
		await new Promise<void>((resolve) => fileUploadSlotWaiters.push(resolve));
	}

	try {
		await processor();
	} finally {
		const nextWaiter = fileUploadSlotWaiters.shift();
		if (nextWaiter) nextWaiter();
		else availableFileUploadSlots++;
	}
}

export class AimdConcurrencyController {
	public readonly maxConcurrency: number;
	private currentValue: number;
	private cleanStreak = 0;
	private readonly sessionId?: string;
	private readonly conversationId?: string;
	private readonly onAdjusted?: (adjustment: { direction: "up" | "down"; current: number; max: number }) => void;

	constructor(
		maxConcurrency: number,
		private readonly telemetry: UploadTelemetry,
		context?: {
			sessionId?: string;
			conversationId?: string;
			onAdjusted?: (adjustment: { direction: "up" | "down"; current: number; max: number }) => void;
		}
	) {
		const ceiling = Math.floor(maxConcurrency);
		this.maxConcurrency = Number.isFinite(ceiling) ? Math.max(MIN_CONCURRENCY, ceiling) : MIN_CONCURRENCY;
		this.currentValue = this.maxConcurrency;
		this.sessionId = context?.sessionId;
		this.conversationId = context?.conversationId;
		this.onAdjusted = context?.onAdjusted;
	}

	get current(): number {
		return this.currentValue;
	}

	/**
	 * Emit telemetry without letting the sink change a control decision (break-it rounds 3-4).
	 *
	 * Every method below applies its AIMD state transition BEFORE logging and routes the log through
	 * here, because the reverse order meant a throwing sink skipped the state change entirely: a chunk
	 * drop logged first and halved concurrency second, so a failing logger left concurrency high and
	 * the congestion response silently did not happen.
	 */
	private logSafely(emit: () => void): void {
		try {
			emit();
		} catch {
			// Deliberately swallowed and deliberately not re-logged: the logger is what just failed.
		}
	}

	recordDrop(ctx?: { code?: string; chunkIndex?: number }): void {
		const nextConcurrency = Math.max(MIN_CONCURRENCY, Math.floor(this.currentValue / 2));
		this.cleanStreak = 0;
		const didAdjust = nextConcurrency !== this.currentValue;
		if (didAdjust) this.currentValue = nextConcurrency;
		this.logSafely(() =>
			this.telemetry.logWarn("recording.fileUpload.chunkDrop", {
				code: ctx?.code,
				chunkIndex: ctx?.chunkIndex,
				sessionId: this.sessionId,
				conversationId: this.conversationId
			})
		);
		if (didAdjust) {
			this.logSafely(() =>
				this.telemetry.logInfo("recording.fileUpload.concurrencyAdjusted", {
					direction: "down",
					value: nextConcurrency,
					sessionId: this.sessionId,
					conversationId: this.conversationId
				})
			);
			this.onAdjusted?.({ direction: "down", current: nextConcurrency, max: this.maxConcurrency });
		}
	}

	/**
	 * LSP-18795: a chunk failed in a way that retrying the same chunk can never fix, and it is
	 * outranking a transient verdict that was reported first.
	 *
	 * Logged rather than left implicit because the bug being fixed was precisely a discarded error:
	 * the old catch dropped the second failure without reaching an else branch, so a recurrence could
	 * only be reconstructed from the absence of a retry. `supersededCode` names what the call WOULD
	 * have thrown, which is the one fact that distinguishes this path from an upload that only ever
	 * saw a permanent failure. Carries the controller's session/conversation ids so the line joins up
	 * with the surrounding chunkDrop and concurrencyAdjusted events for the same upload.
	 */
	recordPermanentVerdict(ctx: { code?: string; supersededCode?: string; chunkIndex?: number }): void {
		this.logSafely(() =>
			this.telemetry.logWarn("recording.fileUpload.chunkPermanentVerdict", {
				code: ctx.code,
				supersededCode: ctx.supersededCode,
				chunkIndex: ctx.chunkIndex,
				sessionId: this.sessionId,
				conversationId: this.conversationId
			})
		);
	}

	recordSuccess(): void {
		this.cleanStreak++;
		if (this.cleanStreak >= RAMP_UP_AFTER && this.currentValue < this.maxConcurrency) {
			this.currentValue++;
			this.cleanStreak = 0;
			this.logSafely(() =>
				this.telemetry.logInfo("recording.fileUpload.concurrencyAdjusted", {
					direction: "up",
					value: this.currentValue,
					sessionId: this.sessionId,
					conversationId: this.conversationId
				})
			);
			this.onAdjusted?.({ direction: "up", current: this.currentValue, max: this.maxConcurrency });
		}
	}
}

export async function processWithAdaptiveConcurrency<T>(
	items: T[],
	controller: AimdConcurrencyController,
	processor: (item: T, index: number, markDurableProgress: () => void) => Promise<void>,
	options?: { verdictUpgradeGraceMs?: number }
): Promise<void> {
	// Sanitized the same way the controller sanitizes its ceiling: a NaN or negative override reaches
	// setTimeout as ~0 and collapses the upgrade window, silently restoring the first-wins bug this
	// ticket fixes (break-it round 4).
	const requestedGraceMs = options?.verdictUpgradeGraceMs ?? VERDICT_UPGRADE_GRACE_MS;
	const verdictUpgradeGraceMs =
		Number.isFinite(requestedGraceMs) && requestedGraceMs >= 0
			? Math.min(requestedGraceMs, MAX_TIMER_DELAY_MS)
			: VERDICT_UPGRADE_GRACE_MS;
	// Array.from, not items.map: map SKIPS holes in a sparse array, so a sparse `items` produced a
	// short pendingIndexes, shift() returned undefined, and the pool read that as queue exhaustion and
	// RESOLVED with valid later items never processed (break-it round 2). driverChunkLoop only ever
	// passes a dense array today, so this is defence in depth — but "resolves successfully while a
	// chunk is unprocessed" is the same silent-success class as the falsy-verdict defect above, and it
	// is one call away from being reachable if a caller ever builds indexes with a filter or a delete.
	const pendingIndexes = Array.from({ length: items.length }, (_item, index) => index);
	const inFlightIndexes = new Set<number>();
	const actualConcurrency = Math.min(controller.maxConcurrency, items.length);
	let activeCount = 0;
	let successesSinceLastDrop = 0;
	// The failure that stopped the pool. Stays first-wins: it is a control-flow signal, and letting a
	// later error rewrite it would change which chunks get requeued.
	let terminalError: unknown = NO_VERDICT;
	// LSP-18795: the first failure that retrying cannot fix, kept sticky and separate from
	// terminalError so it can be observed even when a sibling already stopped the pool.
	//
	// ChunkNetworkError is this module's only transient class — it is the one the recordDrop/requeue
	// path below is built around, and every other error already lands in the immediately-terminal
	// branch. So "not a ChunkNetworkError" IS the permanence test here, and expressing it that way
	// means a new permanent class (uploadChunk.ts's MissingEtagError, LSP-18742) inherits the priority
	// automatically instead of silently re-acquiring the first-wins bug.
	let permanentError: unknown = NO_VERDICT;
	// The two signals the join below races against.
	const terminalVerdict = createSignal();
	const permanentVerdict = createSignal();
	// Every write to terminalError goes through here so the join's grace clock can never be started
	// by one assignment site and missed by another.
	const setTerminalError = (error: unknown): void => {
		if (terminalError !== NO_VERDICT) return;
		terminalError = error;
		terminalVerdict.signal();
	};
	let parkedResolvers: Array<() => void> = [];
	const wakeParkedWorkers = (): void => {
		const resolvers = parkedResolvers;
		parkedResolvers = [];
		for (const resolve of resolvers) resolve();
	};
	const waitForAdmission = async (): Promise<void> => {
		await new Promise<void>((resolve) => parkedResolvers.push(resolve));
	};
	const takeNextIndex = async (): Promise<number | undefined> => {
		while (terminalError === NO_VERDICT) {
			if (pendingIndexes.length === 0) {
				if (activeCount === 0) return undefined;
				await waitForAdmission();
				continue;
			}
			if (activeCount < controller.current) {
				const nextIndex = pendingIndexes.shift();
				if (nextIndex === undefined) return undefined;
				if (inFlightIndexes.has(nextIndex)) {
					pendingIndexes.push(nextIndex);
					await waitForAdmission();
					continue;
				}
				activeCount++;
				inFlightIndexes.add(nextIndex);
				return nextIndex;
			}
			await waitForAdmission();
		}
		throw terminalError;
	};
	const processWorker = async (): Promise<void> => {
		while (terminalError === NO_VERDICT) {
			const index = await takeNextIndex();
			if (index === undefined) return;
			if (terminalError !== NO_VERDICT) {
				activeCount--;
				inFlightIndexes.delete(index);
				wakeParkedWorkers();
				throw terminalError;
			}
			if (activeCount > controller.current) {
				activeCount--;
				inFlightIndexes.delete(index);
				pendingIndexes.unshift(index);
				wakeParkedWorkers();
				continue;
			}
			let processorEnded = false;
			try {
				let progressCredited = false;
				const markDurableProgress = (): void => {
					// A worker can finish its durable checkpoint and then remain in its processor while
					// another worker fails. Credit that observable progress immediately so the concurrent
					// transient failure is requeued instead of being mistaken for the first failure. The
					// guard prevents a late callback or an explicit callback plus normal return from double
					// counting one item.
					if (processorEnded || progressCredited || terminalError !== NO_VERDICT) return;
					progressCredited = true;
					// Guarded like the other two controller calls (break-it round 4): a throwing telemetry
					// sink here was caught by the catch below as though the CHUNK had failed, turning an
					// entirely successful batch into a permanent failure. It matters more now that the
					// caller invokes this callback itself — a throw would surface inside the processor.
					reportSafely(() => controller.recordSuccess());
					successesSinceLastDrop++;
				};
				await processor(items[index], index, markDurableProgress);
				// Existing callers do not yet expose a durable checkpoint callback. Preserve their
				// completion semantics until they migrate, while upload-loop callers credit at the
				// checkpoint boundary above.
				markDurableProgress();
				processorEnded = true;
			} catch (error) {
				// A callback retained by a failed processor must not credit a later worker after this
				// item has entered the error path.
				processorEnded = true;
				// Reading the item back is LABELLING, not classification, so it must not be able to change the
				// verdict (break-it round 8). A container whose element accessor threw a ChunkNetworkError
				// during this reread used to replace an already-recorded permanent verdict with a transient
				// one — the exact downgrade AC3 forbids, arriving through the telemetry path.
				let reportedChunkIndex = index;
				try {
					if (typeof items[index] === "number") reportedChunkIndex = items[index] as number;
				} catch {
					// Fall back to the positional index; a label is never worth losing a verdict over.
				}
				// Classified ONCE, before any branch reads it: isChunkNetworkError is total, and calling it
				// twice would let a hostile throwable answer differently in the two branches.
				const isTransient = isChunkNetworkError(error);
				// Permanence is the residue after BOTH exclusions. Deriving it from `!isTransient` alone was
				// how an abandon signal got ranked as a permanent verdict in the first place.
				const isPermanent = !isTransient && !isAbandonSignal(error);
				// Recorded BEFORE the terminalError gate, which is the whole fix: this used to sit inside
				// the gate, so a permanent verdict arriving second was dropped with no log and no branch.
				if (permanentError === NO_VERDICT && isPermanent) {
					permanentError = error;
					permanentVerdict.signal();
					if (terminalError !== NO_VERDICT) {
						const supersededVerdict = terminalError;
						reportSafely(() =>
							controller.recordPermanentVerdict({
								code: safeErrorLabel(error),
								supersededCode: safeErrorLabel(supersededVerdict),
								chunkIndex: reportedChunkIndex
							})
						);
					}
				} else if (
					permanentError !== NO_VERDICT &&
					!isConfirmedMigrationIntegrityError(permanentError) &&
					isConfirmedMigrationIntegrityError(error)
				) {
					permanentError = error;
					permanentVerdict.signal();
				}
				if (terminalError === NO_VERDICT) {
					if (isTransient) {
						reportSafely(() =>
							controller.recordDrop({
								code: "ChunkNetworkError",
								chunkIndex: reportedChunkIndex
							})
						);
						if (successesSinceLastDrop === 0) setTerminalError(error);
						else {
							pendingIndexes.push(index);
							successesSinceLastDrop = 0;
						}
					} else setTerminalError(error);
				}
			} finally {
				activeCount--;
				inFlightIndexes.delete(index);
				wakeParkedWorkers();
			}
			if (terminalError !== NO_VERDICT) throw terminalError;
		}
	};
	const workers = Array.from({ length: actualConcurrency }, () =>
		// A worker can also reject from OUTSIDE the per-chunk catch — reading controller.current, say,
		// which the admission loop does with no try around it. Such an error is not a ChunkNetworkError,
		// so by this module's own permanence test it outranks a transient verdict, and it has to be
		// RANKED rather than merely kept as a last-resort fallback (break-it round 10): the join throws
		// the strongest recorded verdict before it ever inspects the settled outcomes, so a transient
		// recorded by one worker used to bury a permanent rejection from another.
		processWorker().catch((error: unknown) => {
			if (permanentError === NO_VERDICT && isPermanentVerdict(error)) {
				permanentError = error;
				permanentVerdict.signal();
			} else if (
				permanentError !== NO_VERDICT &&
				!isConfirmedMigrationIntegrityError(permanentError) &&
				isConfirmedMigrationIntegrityError(error)
			) {
				permanentError = error;
				permanentVerdict.signal();
			}
			// Seeded as the terminal verdict too, not just ranked (break-it round 11). Ranking alone left
			// the pool running: the other workers never saw a verdict, so they kept admitting chunks and
			// mutating AIMD, and — worse — a TRANSIENT outside-catch rejection never started the grace
			// clock, so the bounded wait became unbounded again and the call hung on a parked sibling.
			// First-wins, so a verdict the per-chunk catch already recorded still stands.
			setTerminalError(error);
			throw error;
		})
	);
	// LSP-18795: allSettled, not all. Promise.all rejects the moment the first worker throws, so a
	// sibling that was still mid-flight never got its error caught at all and the upgrade above would
	// be dead code. allSettled also means no worker rejection can go unhandled, whichever of the three
	// race outcomes below wins — handlers are attached to every worker here, synchronously.
	const settled = Promise.allSettled(workers);
	// The grace clock starts when a verdict first exists, NOT at join time — starting it here would
	// make a healthy upload longer than the window return early with its chunks unfinished.
	let graceTimer: ReturnType<typeof setTimeout> | undefined;
	const upgradeGraceElapsed = terminalVerdict.observed.then(() => {
		// Only the transient-first path waits, so only it arms a timer (break-it round 8). Permanent-first
		// used to arm one and clear it immediately — no observed latency, but it contradicted the
		// contract stated on VERDICT_UPGRADE_GRACE_MS, and a comment that overclaims is how the next
		// person reasons their way into a real bug.
		if (permanentError !== NO_VERDICT) return undefined;
		return new Promise<void>((resolve) => {
			graceTimer = setTimeout(resolve, verdictUpgradeGraceMs);
		});
	});
	try {
		// Whichever comes first: every worker done, a permanent verdict observed (nothing can outrank
		// it, so there is nothing left to wait for), or the grace window elapsing on a transient
		// verdict. The last of those is what keeps an offline-parked sibling from suppressing a
		// terminal failure indefinitely — see VERDICT_UPGRADE_GRACE_MS.
		await Promise.race([settled, permanentVerdict.observed, upgradeGraceElapsed]);
	} finally {
		if (graceTimer) clearTimeout(graceTimer);
	}
	// Permanent beats transient, for the same reason as uploadChunk.ts's final throw: a transient
	// classification is retried unbounded by both drivers, and unbounded retry of a condition that can
	// never succeed IS the defect.
	// Every worker rejection is seeded above, so a rejection always leaves a verdict here and allSettled
	// can never turn a failed batch into a clean return. There used to be a scan of `settled` as a
	// backstop for that; round 11's setTerminalError in the worker catch made it unreachable, and an
	// unreachable guard is worse than no guard — it reads as protection while proving nothing, which is
	// exactly how the swallow test for it went vacuous four times. Deleted rather than kept for comfort.
	// An abandon signal that got in first is never overridden: it means another scope owns this session
	// (or the caller cancelled), so writing a terminal verdict over it would act on a session we no
	// longer own.
	const strongestError = isAbandonSignal(terminalError) || permanentError === NO_VERDICT ? terminalError : permanentError;
	if (strongestError !== NO_VERDICT) throw strongestError;
}

export async function processWithConcurrency<T>(
	items: T[],
	concurrency: number,
	processor: (item: T, index: number) => Promise<void>
): Promise<void> {
	let currentIndex = 0;
	let hasError = false;
	const requestedConcurrency = Math.floor(concurrency);
	const actualConcurrency = Math.min(
		Number.isFinite(requestedConcurrency) ? Math.max(MIN_CONCURRENCY, requestedConcurrency) : MIN_CONCURRENCY,
		items.length
	);
	const processNext = async (): Promise<void> => {
		while (currentIndex < items.length && !hasError) {
			const index = currentIndex++;
			await processor(items[index], index);
		}
	};
	const workers = Array.from({ length: actualConcurrency }, () =>
		processNext().catch((error) => {
			hasError = true;
			throw error;
		})
	);
	await Promise.all(workers);
}
